import type { CapabilityManifest, DeclarativeCapabilityAuthoringRegistration, I18nText, JsonObject, RevisionRef, StableRef, ViewProviderDescriptor } from '@inf-monkeys-tech/monkeys';
import { canonicalContentHash, DeclarativeCapabilityAuthoringRegistrationSchema, ProductContextSchema, ProductSurfaceSchema } from '@inf-monkeys-tech/monkeys';
import type { CapabilityCatalogCategory, CapabilityOntologyCompatibility, CapabilityEditorMetadata, DesignCapabilityCatalogEntry } from './contracts';

const VERSION = "1";
const OWNER = "monkeys-design";
const PRODUCT_CONTEXTS = ProductContextSchema.options;
const TOKEN_REFS = [
  "semantic.color.background",
  "semantic.color.foreground",
  "semantic.color.border",
  "semantic.spacing.global",
  "semantic.radius.base",
] as const;

const GLOBAL_VISIBILITY = "global" as const;
const SCHEMA_VERSION = 1;
const REVISION = 1;

export interface PropertyFieldDefinition {
  path: string;
  label: I18nText;
  control: string;
  required: boolean;
  advanced: boolean;
}

export interface OutputPortDefinition {
  name: string;
  schemaRef: string;
  schemaDocument: JsonObject;
  allowedCommandKinds?: readonly string[];
  requiredPermissionCodes?: readonly string[];
}

interface EntryOptions {
  id: string;
  version?: string;
  revision?: number;
  authoringRevision?: number;
  schemaRevision?: number;
  rendererKey: string;
  kind: CapabilityManifest["kind"];
  category: CapabilityCatalogCategory;
  labelKey: string;
  descriptionKey: string;
  authoringLabel: I18nText;
  authoringDescription: I18nText;
  inputPort: string;
  schemaRef: string;
  schemaDocument: JsonObject;
  compatibility?: readonly CapabilityOntologyCompatibility[];
  slots: readonly string[];
  loading?: CapabilityManifest["runtime"]["loading"];
  sideEffects?: CapabilityManifest["runtime"]["sideEffects"];
  supportedPageTypes: readonly string[];
  supportedSurfaces: ViewProviderDescriptor["supportedSurfaces"];
  authoringSurfaces?: DeclarativeCapabilityAuthoringRegistration["supportedSurfaces"];
  propertySchema?: JsonObject;
  uiSchema?: JsonObject;
  propertyFields?: readonly PropertyFieldDefinition[];
  outputPorts?: readonly OutputPortDefinition[];
  frameOwner?: ViewProviderDescriptor["frameOwner"];
  virtualized?: boolean;
  budgetMs?: number;
  editorEligible?: boolean;
}

export const i18n = (english: string, chinese: string): I18nText => ({
  defaultLocale: "en-US",
  values: { "en-US": english, "zh-CN": chinese },
});

const stableRef = (kind: string, id: string): StableRef => ({
  kind,
  id,
  ownerRepo: OWNER,
  visibility: GLOBAL_VISIBILITY,
});

const revisionRef = (
  kind: string,
  id: string,
  content: unknown,
  revision = REVISION,
): RevisionRef => ({
  ...stableRef(kind, id),
  revision,
  schemaVersion: SCHEMA_VERSION,
  contentHash: canonicalContentHash(content),
});

const JSON_SCHEMA_DIALECT = "https://json-schema.org/draft/2020-12/schema";

export const jsonSchema = (id: string, definition: JsonObject): JsonObject => ({
  $schema: JSON_SCHEMA_DIALECT,
  $id: id,
  ...definition,
});

const schemaDocumentEntry = (
  id: string,
  document: JsonObject,
  revision = REVISION,
) => ({
  schemaRevisionRef: revisionRef("schema", id, document, revision),
  document,
});

const schemaDocumentKey = ({
  schemaRevisionRef,
}: ReturnType<typeof schemaDocumentEntry>) =>
  `${schemaRevisionRef.ownerRepo}:${schemaRevisionRef.id}@${schemaRevisionRef.revision}:${schemaRevisionRef.contentHash}`;

export const createDesignCapabilityEntry = ({
  id,
  version = VERSION,
  revision = REVISION,
  authoringRevision = revision,
  schemaRevision = REVISION,
  rendererKey,
  kind,
  category,
  labelKey,
  descriptionKey,
  authoringLabel,
  authoringDescription,
  inputPort,
  schemaRef,
  schemaDocument,
  compatibility = [],
  slots,
  loading = "eager",
  sideEffects = [],
  supportedPageTypes,
  supportedSurfaces,
  authoringSurfaces = ProductSurfaceSchema.options,
  propertySchema = {
    type: "object",
    additionalProperties: false,
    properties: {},
  },
  uiSchema = { order: [] },
  propertyFields = [],
  outputPorts = [],
  frameOwner = "host",
  virtualized = false,
  budgetMs,
  editorEligible = true,
}: EntryOptions): DesignCapabilityCatalogEntry => {
  const providerId = `${id}.provider`;
  const providerRef = {
    kind: "view-provider",
    id: providerId,
    version,
    ownerRepo: OWNER,
  };
  const manifest: CapabilityManifest = {
    contract: "CapabilityManifest",
    id,
    capabilityVersion: version,
    ownerRepo: OWNER,
    kind,
    displayName: labelKey,
    description: descriptionKey,
    ports: {
      inputs: [{ name: inputPort, schemaRef, required: true, multiple: false }],
      outputs: outputPorts.map(({ name, schemaRef }) => ({
        name,
        schemaRef,
        required: false,
        multiple: false,
      })),
    },
    runtime: {
      providerBindings: [
        {
          providerRef,
          productContexts: [...PRODUCT_CONTEXTS],
          priority: 100,
        },
      ],
      loading,
      stateOwner: "host",
      sideEffects: [...sideEffects],
    },
    placement: {
      surfaces: [...supportedSurfaces],
      slots: [...slots],
      variants: [],
      tokenRefs: [...TOKEN_REFS],
    },
    accessibility: {
      keyboardModel: `${id}.keyboard`,
      focusModel: `${id}.focus`,
      labelContract: `${id}.labels`,
    },
    observability: {
      eventNamespace: id,
      metrics: ["load.duration", "load.outcome"],
      evidenceRefs: ["render-node.evidenceRef"],
      ...(budgetMs === undefined ? {} : { performanceBudgetMs: budgetMs }),
    },
  };
  const provider: ViewProviderDescriptor = {
    contract: "ViewProviderDescriptor",
    providerId,
    providerVersion: version,
    ownerRepo: OWNER,
    capabilityRef: {
      kind: "capability",
      id,
      version,
      ownerRepo: OWNER,
    },
    rendererKey,
    renderModelSchemaRef: schemaRef,
    loading,
    stateOwner: "host",
    supportedPageTypes: [...supportedPageTypes],
    supportedSurfaces,
    frameOwner,
    sideEffects: [...sideEffects],
    ...(sideEffects.length > 0
      ? {
          sideEffectAdapterRef: {
            kind: "side-effect-adapter",
            id: `${id}.side-effects`,
            version,
            ownerRepo: OWNER,
          },
        }
      : {}),
    lifecycle: {
      preserveMount: kind === "professional-provider" || frameOwner === "provider",
      preserveScroll: kind === "professional-provider",
      focusModel: `${id}.focus`,
    },
    performance: {
      lazy: loading !== "eager",
      virtualized,
      ...(budgetMs === undefined ? {} : { budgetMs }),
    },
  };
  const editor: CapabilityEditorMetadata = {
    eligible: editorEligible,
    category,
    labelKey,
    descriptionKey,
    slots,
    ontologyCompatibility: compatibility,
  };
  const propertySchemaDocument = jsonSchema(`${id}.properties`, propertySchema);
  const uiSchemaDocument = jsonSchema(`${id}.properties.ui`, {
    type: "object",
    additionalProperties: false,
    properties: {
      order: {
        type: "array",
        items: { type: "string" },
      },
    },
    required: ["order"],
    default: uiSchema,
  });
  const propertySchemaEntry = schemaDocumentEntry(
    `${id}.properties`,
    propertySchemaDocument,
    schemaRevision,
  );
  const uiSchemaEntry = schemaDocumentEntry(
    `${id}.properties.ui`,
    uiSchemaDocument,
    schemaRevision,
  );
  const inputSchemaEntry = schemaDocumentEntry(
    schemaRef,
    schemaDocument,
    schemaRevision,
  );
  const outputSchemaEntries = outputPorts.map((port) =>
    schemaDocumentEntry(port.schemaRef, port.schemaDocument, schemaRevision),
  );
  const schemaDocuments = [
    propertySchemaEntry,
    uiSchemaEntry,
    inputSchemaEntry,
    ...outputSchemaEntries,
  ].filter(
    (entry, index, entries) =>
      entries.findIndex(
        (candidate) =>
          schemaDocumentKey(candidate) === schemaDocumentKey(entry),
      ) === index,
  );
  const unsignedRegistration = {
    capabilityRevisionRef: revisionRef("capability", id, manifest, revision),
    providerRevisionRef: revisionRef(
      "view-provider",
      providerId,
      provider,
      revision,
    ),
    propertySchemaRevisionRef: propertySchemaEntry.schemaRevisionRef,
    uiSchemaRevisionRef: uiSchemaEntry.schemaRevisionRef,
    editorEligible,
    category,
    label: authoringLabel,
    description: authoringDescription,
    supportedSurfaces: [...authoringSurfaces],
    allowedSlots: [...slots],
    allowedParentCapabilityRefs: [] as StableRef[],
    allowedChildCapabilityRefs: [] as StableRef[],
    inputPorts: manifest.ports.inputs.map((port) => ({
      name: port.name,
      schemaRevisionRef: inputSchemaEntry.schemaRevisionRef,
      required: port.required,
      multiple: port.multiple,
    })),
    outputPorts: manifest.ports.outputs.map((port) => {
      const schema = outputSchemaEntries.find(
        (entry) => entry.schemaRevisionRef.id === port.schemaRef,
      );
      if (!schema)
        throw new TypeError(
          `Missing output schema document for ${port.schemaRef}.`,
        );
      return {
        name: port.name,
        schemaRevisionRef: schema.schemaRevisionRef,
        required: port.required,
        multiple: port.multiple,
      };
    }),
    allowedSideEffects: [...sideEffects],
    ontologyRequirements: compatibility.map((requirement) => ({
      inputPort: requirement.inputPort,
      renderModelSchemaRevisionRef: inputSchemaEntry.schemaRevisionRef,
      modes: [requirement.mode],
      requiredPermissionCodes: [],
    })),
    actionRequirements: outputPorts.map((port) => ({
      outputPort: port.name,
      allowedCommandKinds: [...(port.allowedCommandKinds ?? [])],
      requiredPermissionCodes: [...(port.requiredPermissionCodes ?? [])],
    })),
    propertyFields: propertyFields.map((field) => ({ ...field })),
    schemaDocuments,
  } satisfies Omit<
    DeclarativeCapabilityAuthoringRegistration,
    "registrationRevisionRef" | "sourceContentHash"
  >;
  const sourceContentHash = canonicalContentHash(unsignedRegistration);
  const declarativeRegistration =
    DeclarativeCapabilityAuthoringRegistrationSchema.parse({
      ...unsignedRegistration,
      registrationRevisionRef: {
        ...stableRef("design-capability-registration", `${id}.authoring`),
        revision: authoringRevision,
        schemaVersion: SCHEMA_VERSION,
        contentHash: sourceContentHash,
      },
      sourceContentHash,
    });
  return { manifest, provider, editor, declarativeRegistration };
};

